import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import i18n from '../../i18n/i18n';
import { BULLMQ_JOBS } from '../../constant/message-queue.constant';
import { IBulkUploadState } from '../../interface/bulkUpload.interface';
import { downloadTemplate, uploadFile } from '../action/bulkUpload.action';

/**
 * Initial state for bulk upload slice
 */
const initialState: IBulkUploadState = {
  currentModule: null,
  loading: false,
  error: null,
  uploadResult: null,
};

/**
 * Bulk Upload Redux Slice
 * Manages centralized state for bulk upload operations across all modules
 */
const bulkUploadSlice = createSlice({
  name: 'bulkUpload',
  initialState,
  reducers: {
    /**
     * Set the current module for bulk upload
     */
    setCurrentModule: (state, action: PayloadAction<BULLMQ_JOBS>) => {
      state.currentModule = action.payload;
    },
    /**
     * Reset upload result (clear success/error messages)
     */
    resetUploadResult: (state) => {
      state.uploadResult = null;
      state.error = null;
    },
    /**
     * Reset entire bulk upload state
     */
    resetBulkUploadState: () => initialState,
  },
  extraReducers: (builder) => {
    // Download Template Cases
    builder
      .addCase(downloadTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(downloadTemplate.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(downloadTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_download_template', 'Failed to download template');
      });

    // Upload File Cases
    builder
      .addCase(uploadFile.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.uploadResult = null;
      })
      .addCase(uploadFile.fulfilled, (state, action) => {
        state.loading = false;
        state.uploadResult = action.payload;
      })
      .addCase(uploadFile.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || i18n.t('toast.failed_to_upload_file', 'Failed to upload file');
      });
  },
});

export const { setCurrentModule, resetUploadResult, resetBulkUploadState } =
  bulkUploadSlice.actions;

export default bulkUploadSlice.reducer;
